import { useEffect, useRef, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { apiRequest } from '../../api/client';
import { canChat } from '../../lib/permissions';
import { capAi } from '../../lib/capabilities';
import { chatText } from '../../i18n/chat';
import type { NodeId } from '../../lib/types';

/**
 * The documentation chatbot, bottom-right.
 *
 * Answers from what THIS USER is allowed to see — the server does that filtering, which is
 * why the client never passes a scope — and highlights the nodes an answer drew on, so the
 * reply and the map agree about what it was talking about.
 *
 * The highlight is map state (`chatHighlight` feeds `computeForceOpen`, which reveals hits
 * inside collapsed branches), so setting and clearing it goes through actions. Closing the
 * dock clears it: a highlight with no visible explanation is just an unexplained selection.
 *
 * MARKDOWN IS RENDERED, NOT INTERPRETED. `mdLite` handles paragraphs, bullet lists, inline
 * code, bold and links, and nothing else. It is deliberately not a markdown library: the
 * output goes into the page, the input comes from a model, and the smallest grammar that
 * covers what the model actually emits is the one with the least to get wrong.
 */

interface Citation {
  title?: string;
  docId?: string;
  nodeId?: NodeId;
}

interface Message {
  id: number;
  who: 'user' | 'bot';
  /** Plain text for a user message; already-parsed blocks for a bot answer. */
  text?: string;
  blocks?: Block[];
  citations?: Citation[];
  thinking?: boolean;
}

type Inline = { kind: 'text' | 'code' | 'bold'; text: string } | { kind: 'link'; text: string; href: string };
type Block = { kind: 'p'; lines: Inline[][] } | { kind: 'ul'; items: Inline[][] };

/**
 * The inline grammar: `code`, **bold**, [text](https://…).
 *
 * Returned as data rather than HTML so React renders it — nothing here is ever passed to
 * dangerouslySetInnerHTML, which is the point. The vanilla version escaped by hand and then
 * un-escaped three patterns, which is one forgotten escape away from injecting a model's
 * output into the page.
 */
export function parseInline(str: string): Inline[] {
  const out: Inline[] = [];
  const re = /`([^`]+)`|\*\*([^*]+)\*\*|\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(str))) {
    if (m.index > last) out.push({ kind: 'text', text: str.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ kind: 'code', text: m[1] });
    else if (m[2] !== undefined) out.push({ kind: 'bold', text: m[2] });
    else out.push({ kind: 'link', text: m[3], href: m[4] });
    last = re.lastIndex;
  }
  if (last < str.length) out.push({ kind: 'text', text: str.slice(last) });
  return out;
}

/** Blank-line-separated blocks; a block whose every line is a bullet becomes a list. */
export function parseBlocks(text: string): Block[] {
  return String(text || '')
    .trim()
    .split(/\n\s*\n/)
    .map((block): Block => {
      const lines = block.split('\n');
      if (lines.length && lines.every((l) => /^\s*[-*]\s+/.test(l))) {
        return { kind: 'ul', items: lines.map((l) => parseInline(l.replace(/^\s*[-*]\s+/, ''))) };
      }
      return { kind: 'p', lines: lines.map(parseInline) };
    });
}

function Inlines({ parts }: { parts: Inline[] }) {
  return (
    <>
      {parts.map((p, i) =>
        p.kind === 'code' ? (
          <code key={i}>{p.text}</code>
        ) : p.kind === 'bold' ? (
          <b key={i}>{p.text}</b>
        ) : p.kind === 'link' ? (
          <a key={i} href={p.href} target="_blank" rel="noopener">
            {p.text}
          </a>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

function Blocks({ blocks }: { blocks: Block[] }) {
  return (
    <div className="chat-md">
      {blocks.map((b, i) =>
        b.kind === 'ul' ? (
          <ul key={i}>
            {b.items.map((item, j) => (
              <li key={j}>
                <Inlines parts={item} />
              </li>
            ))}
          </ul>
        ) : (
          <p key={i}>
            {b.lines.map((line, j) => (
              <span key={j}>
                {j > 0 ? <br /> : null}
                <Inlines parts={line} />
              </span>
            ))}
          </p>
        ),
      )}
    </div>
  );
}

export function ChatDock({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const nextId = useRef(1);

  const allowed = canChat(s.perm) && capAi(s.caps, 'chat');
  const t = (k: Parameters<typeof chatText>[0]) => chatText(k, s.currentLocale);

  // The host is #chatdock. Its `open` class drives the panel, and its display is what the
  // vanilla applyPermissions() used to set by hand — the region owns both now, so the two
  // cannot disagree about whether the dock exists.
  useEffect(() => {
    host.classList.toggle('open', open);
    host.style.display = allowed ? '' : 'none';
  }, [open, host, allowed]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    // Closing takes the highlight with it: nodes lit up with no visible reason are just an
    // unexplained selection.
    //
    // Gated on `booted`, like every other first-mount action call in the port: this region
    // mounts before boot() has answered anything — main.tsx mounts every region and then starts
    // end — so the FIRST run of this effect, with `open` false and nothing lit, was asking for
    // a function that did not exist yet and logging a bridge error on every signed-in load.
    else if (s.booted) callAction('setChatHighlight', []);
  }, [open, s.booted]);

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Losing the right mid-conversation closes it rather than leaving a dock that 403s.
  useEffect(() => {
    if (!allowed && open) setOpen(false);
  }, [allowed, open]);

  const send = async () => {
    const q = draft.trim();
    if (!q || busy) return;
    setDraft('');
    const userId = nextId.current++;
    const thinkingId = nextId.current++;
    setMessages((m) => [
      ...m,
      { id: userId, who: 'user', text: q },
      { id: thinkingId, who: 'bot', text: t('thinking'), thinking: true },
    ]);
    callAction('setChatHighlight', []);
    setBusy(true);

    try {
      const data = await apiRequest<{ answer?: string; citations?: Citation[]; nodeIds?: NodeId[] }>(
        '/api/chat',
        { method: 'POST', body: { message: q, lang: s.currentLocale } },
      );
      setMessages((m) =>
        m
          .filter((x) => x.id !== thinkingId)
          .concat({
            id: nextId.current++,
            who: 'bot',
            blocks: parseBlocks(data.answer || ''),
            citations: data.citations || [],
          }),
      );
      if (Array.isArray(data.nodeIds) && data.nodeIds.length) {
        callAction('setChatHighlight', data.nodeIds);
      }
    } catch {
      setMessages((m) =>
        m.filter((x) => x.id !== thinkingId).concat({ id: nextId.current++, who: 'bot', text: t('error') }),
      );
    } finally {
      setBusy(false);
    }
  };

  const openCitation = (c: Citation) => {
    if (c.docId) {
      callAction('openDocInCurrentMode', c.docId, s.docTitles[c.docId] || c.title || 'Documentation', c.nodeId);
    } else if (c.nodeId) {
      callAction('setChatHighlight', [c.nodeId]);
    }
  };

  if (!allowed) return null;

  return (
    <>
      <button
        className="chat-launch"
        id="chat-launch"
        title="Ask the documentation"
        onClick={() => setOpen(true)}
      >
        <svg className="ico">
          <use href="#i-ai" />
        </svg>
        <span id="chat-launch-label">{t('title')}</span>
      </button>

      <div className="chat-panel" id="chat-panel">
        <div className="chat-head">
          <span className="chat-title" id="chat-title">
            <svg className="ico">
              <use href="#i-ai" />
            </svg>
            <span id="chat-title-label">{t('title')}</span>
          </span>
          <button className="icobtn" id="chat-collapse" title="Close" onClick={() => setOpen(false)}>
            <svg className="ico" style={{ width: 15, height: 15 }}>
              <use href="#i-x" />
            </svg>
          </button>
        </div>

        <div className="chat-log" id="chat-log" ref={logRef}>
          {!messages.length ? <div className="chat-empty">{t('empty')}</div> : null}
          {messages.map((m) => (
            <div key={m.id} className={`chat-msg ${m.who}${m.thinking ? ' thinking' : ''}`}>
              {m.blocks ? <Blocks blocks={m.blocks} /> : m.text}
              {m.citations && m.citations.length ? (
                <div className="chat-cites">
                  {m.citations.map((c, i) => (
                    <span key={i} className="chat-cite" onClick={() => openCitation(c)}>
                      <span className="cd" />
                      {c.title || 'Source'}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <div className="chat-input">
          <textarea
            ref={inputRef}
            id="chat-text"
            rows={1}
            placeholder={t('placeholder')}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              // Grows with the question, to a point: the log matters more than the box.
              e.target.style.height = '';
              e.target.style.height = `${Math.min(120, e.target.scrollHeight)}px`;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <button className="pbtn primary" id="chat-send" disabled={busy} onClick={send}>
            {t('send')}
          </button>
        </div>
      </div>
    </>
  );
}
